import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { User } from '../../types';
import { RootState } from '../../app/store';
import * as authService from '../../services/authService';

interface AuthState {
  isAuthenticated: boolean;
  user: User | null;
  temporaryUserId: string | null;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
}

const initialState: AuthState = {
  isAuthenticated: false,
  user: null,
  temporaryUserId: null,
  status: 'idle',
  error: null,
};

export const login = createAsyncThunk<User, { mode: 'login' | 'register', credentials: any }, { state: RootState }>(
  'auth/login',
  async ({ mode, credentials }, { dispatch, getState }) => {
    const { temporaryUserId } = getState().auth;

    const payload = { ...credentials, temporaryUserId };

    const user = mode === 'login' 
      ? await authService.login(payload)
      : await authService.register(payload);
    dispatch(setAuthCredentials(user));
    return user;
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setTemporaryUserId: (state, action: PayloadAction<string>) => {
      state.temporaryUserId = action.payload;
    },
    setAuthCredentials: (state, action: PayloadAction<User>) => {
      state.isAuthenticated = true;
      state.user = action.payload;
      state.status = 'succeeded';
    },
    logoutSuccess: (state) => {
      state.isAuthenticated = false;
      state.user = null;
      state.status = 'idle';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(login.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Login failed';
      });
  },
});

export const { setAuthCredentials, logoutSuccess, setTemporaryUserId } = authSlice.actions;

export const selectIsAuthenticated = (state: RootState) => state.auth.isAuthenticated;
export const selectTemporaryUserId = (state: RootState) => state.auth.temporaryUserId;

export default authSlice.reducer;
